import { reset } from 'redux-form';
import { ThunkDispatch } from 'redux-thunk';

import { createPageProgressMap } from '../application/application';

import { getFieldsIdsByPage } from './formUtils.js';
import {
  FORM_FETCH_REQUEST,
  FORM_FETCH_SUCCESS,
  FORM_FETCH_FAILURE,
  FORM_SUBMIT_REQUEST,
  FORM_SUBMIT_SUCCESS,
  FORM_SUBMIT_FAILURE
} from './form.constants';
import { STAGING_FORMS, FORM_NAME } from '../../constants';

export const formFetchRequest = (): FormFetchRequestAction => {
  return {
    type: FORM_FETCH_REQUEST
  };
};

export const formFetchSuccess = (form: Form, formIds: FormIds): FormFetchSuccessAction => {
  return {
    type: FORM_FETCH_SUCCESS,
    form,
    formIds
  };
};

export const formFetchFailure = (error: FormError): FormFetchFailureAction => {
  return {
    type: FORM_FETCH_FAILURE,
    error
  };
};

export const fetchForm = () => {
  return async (dispatch: ThunkDispatch<ReduxState, {}, ReduxAction>) => {
    return new Promise<void>((resolve, reject) => {
      dispatch(formFetchRequest());

      const config: RequestInit = {
        headers: {
          CSPACE: 'forms'
        },
        credentials: 'same-origin'
      };
      let url = `${process.env.REACT_APP_BASE_URL}/formsna?type=channelpartner&cfm_t=channelpartner`;
      if (STAGING_FORMS) {
        url += '&staging=true';
      }

      fetch(url, config)
        .then(response => {
          return response.json();
        })
        .then((form: Form) => {
          const formIdsByPage = getFieldsIdsByPage(form);
          const formIds = formIdsByPage.reduce((acc: string[], fields: string[]) => {
            acc.push(...fields);
            return acc;
          }, []);

          dispatch(formFetchSuccess(form, formIds));
          dispatch(createPageProgressMap(false, formIdsByPage[0]));
          resolve();
        })
        .catch(() => {
          const error: FormError = {
            title: 'Error fetching form',
            content: 'There was an error fetching the form. Please try again.'
          };
          dispatch(formFetchFailure(error));
          reject();
        });
    });
  };
};

export const formSubmitRequest = (): FormSubmitRequestAction => {
  return {
    type: FORM_SUBMIT_REQUEST
  };
};

export const formSubmitSuccess = (): FormSubmitSuccessAction => {
  return {
    type: FORM_SUBMIT_SUCCESS
  };
};

export const formSubmitFailure = (error: FormError): FormSubmitFailureAction => {
  return {
    type: FORM_SUBMIT_FAILURE,
    error
  };
};

export const submitForm = (values: Values) => {
  return async (dispatch: ThunkDispatch<ReduxState, {}, ReduxAction>, getState: GetState) => {
    return new Promise<void>((resolve, reject) => {
      dispatch(formSubmitRequest());

      const state = getState();
      const data = state.customForm.form ? state.customForm.form.data : {};

      const requestBody = {
        ...values,
        ...data
      };

      const config: RequestInit = {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'Content-Type': 'application/json'
        }
      };
      const url = `${process.env.REACT_APP_BASE_URL}/formsna`;

      fetch(url, config)
        .then(response => {
          if (!response.ok) {
            throw new Error();
          }
          return response.json();
        })
        .then(() => {
          dispatch(formSubmitSuccess());
          dispatch(reset(FORM_NAME));
          resolve();
        })
        .catch(() => {
          const error: FormError = {
            title: 'Error submitting the form',
            content: 'There was an error submitting the form. Please try again.'
          };
          dispatch(formSubmitFailure(error));
          reject();
        });
    });
  };
};

const initialState = {
  formFetching: true,
  formSubmitting: false,
  formSubmitSuccess: false
};

export default function reducer(state: FormState = initialState, action: FormAction) {
  switch (action.type) {
    case FORM_FETCH_REQUEST:
      return {
        ...state,
        formFetching: true
      };
    case FORM_FETCH_SUCCESS: {
      const { form, formIds } = action as FormFetchSuccessAction;
      return {
        ...state,
        formFetching: false,
        form,
        formIds
      };
    }
    case FORM_FETCH_FAILURE: {
      const { error: formFetchError } = action as FormFetchFailureAction;
      return {
        ...state,
        formFetching: false,
        formFetchError
      };
    }
    case FORM_SUBMIT_REQUEST:
      return {
        ...state,
        formSubmitting: true
      };
    case FORM_SUBMIT_SUCCESS:
      return {
        ...state,
        formSubmitting: false,
        formSubmitSuccess: true
      };
    case FORM_SUBMIT_FAILURE: {
      const { error: formSubmitError } = action as FormSubmitFailureAction;

      return {
        ...state,
        formSubmitting: false,
        formSubmitError
      };
    }
    default:
      return state;
  }
}
